import { Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'
import { useToast } from '@shared/ui/toast'

import { useLessonCreateStore } from './create/store'
import { useDisciplineStore } from '../store'
import { useLessonStoreApi } from '../../api/lessons/store.hook'
import { useDisciplineRefresh } from '../discipline-refresh.hook'

interface LessonCreateSaveReturn {
  refLessonsUrl: string
  refBalance: ComputedRef<number | null>
  warnWhenNoBalance: () => boolean
  createLesson: () => Promise<boolean>
}

/** Adds the lesson of the form and leads back to the Lessons tab. The page and the tutor modal both end here, so the
 *  balance guard of a paid lesson sits here as well. */
export const useLessonCreateSave = (): LessonCreateSaveReturn => {
  const route = useRoute()
  const toast = useToast()
  const store = useLessonCreateStore()
  const disciplineStore = useDisciplineStore()
  const { refreshDiscipline } = useDisciplineRefresh()

  const disciplineId = route.params.id as string

  const refLessonsUrl = resolveUrl(AppRoute.DisciplinesLessons, { id: disciplineId }) ?? ''

  /** Only the full discipline read carries it; without view-disciplines the counter is unknown and the API alone
   *  refuses a paid lesson with no balance left. */
  const refBalance = computed<number | null>(() => disciplineStore.getDetails?.unscheduled_bookings ?? null)

  const warnWhenNoBalance = (): boolean => {
    if (store.getType !== BookingType.Paid || refBalance.value === null || refBalance.value > 0) return false

    toast.warning({
      title: `Lessons available for booking is: ${refBalance.value}`,
      description: 'Check the Balances page table!',
    })

    return true
  }

  const createLesson = async (): Promise<boolean> => {
    const type = store.getType
    const duration = store.getDuration
    const start = store.getStart
    if (!type || !duration || !start || warnWhenNoBalance()) return false

    const created = await useLessonStoreApi({
      disciplineId,
      payload: {
        type,
        start,
        duration,
        tutor_id: store.getTutorId,
      },
      onStart: () => store.setSaving(true),
      onFinish: () => store.setSaving(false),
    })

    if (!created) return false

    // The new lesson takes one from the balance the discipline tabs show. The Lessons tab's own loader covers that
    // reread; here it would rise over the open tutor modal.
    await navigateTo(refLessonsUrl)
    void refreshDiscipline()

    return true
  }

  return {
    refLessonsUrl,
    refBalance,
    warnWhenNoBalance,
    createLesson,
  }
}
